import React, { useState, useMemo } from 'react';
import { 
  Search, 
  User, 
  BookOpen, 
  FileText, 
  Calendar, 
  MapPin, 
  ArrowRight, 
  X, 
  Sparkles, 
  Tag, 
  Filter,
  CheckCircle2,
  Compass,
  Feather,
  Users,
  Hammer,
  Book,
  Heart
} from 'lucide-react';
import { FamilyMember, Story, ArchiveItem, ArchiveTab } from '../types';
import { isVerifiedHistoricalImage, getPersonInitials } from './PersonDossierModal';

interface ChapterIndexItem {
  id: string;
  tab: ArchiveTab;
  title: string;
  category: string;
  era: string;
  summary: string;
  keywords: string[];
}

const ARCHIVAL_CHAPTERS: ChapterIndexItem[] = [
  {
    id: 'ch-home',
    tab: 'homepage',
    title: 'Author Words & Introduction — Christine Negus',
    category: 'Compilation Memoir & Introduction',
    era: '1850–2021',
    summary: 'Christine Negus memoir on Oakdale, Tom and Annie Denton (née Gibb), headstone surveys, and Wakefield colonisation.',
    keywords: ['Christine', 'Negus', 'Annie Denton', 'Oakdale', 'Waikari', 'Cemetery', 'Wakefield', 'Introduction', 'Pioneers', '1850', '2021', 'Canterbury']
  },
  {
    id: 'ch-journey-suffolk',
    tab: 'journey-suffolk',
    title: 'The Journey: From Suffolk to Canterbury',
    category: 'The Journey',
    era: '1850',
    summary: 'The 1850 sea voyage from Bury St Edmunds, Great Barton, departure from Plymouth, the First Four Ships, and arriving in Lyttelton.',
    keywords: ['Suffolk', 'Canterbury', 'Bury St Edmunds', 'Great Barton', 'Plymouth', 'First Four Ships', 'Lyttelton', 'Sir George Seymour', 'Charlotte Jane', 'Randolph', 'Cressy', 'Voyage', 'Steerage', '1850']
  },
  {
    id: 'ch-journey-essentials',
    tab: 'journey-essentials',
    title: 'The Journey: Steerage Essentials & Outfit Lists',
    category: 'The Journey',
    era: '1850',
    summary: 'Detailed passenger regulations, required clothing outfits, wool blankets, iron utensils, shipboard rations, and steerage life.',
    keywords: ['Outfit', 'Steerage', 'Essentials', 'Packing', 'Provisions', 'Flannel', 'Blankets', 'Utensils', 'Regulations', '1850', 'Diet', 'Rations']
  },
  {
    id: 'ch-journey-why',
    tab: 'journey-why-they-came',
    title: 'The Journey: Why They Came (Wakefield Colonisation)',
    category: 'The Journey',
    era: '1836–1851',
    summary: 'Edward Gibbon Wakefield system, Canterbury Association, British population pressure, assisted immigration, and social vision.',
    keywords: ['Why They Came', 'Edward Gibbon Wakefield', 'Canterbury Association', 'Assisted Immigrants', 'Colonisation', 'Immigration', 'England', '1836', '1850']
  },
  {
    id: 'ch-dentons',
    tab: 'dentons',
    title: 'The Dentons: 9-Part Historical Series',
    category: 'The Dentons',
    era: 'Pre-1850 to WW2',
    summary: '9-part series: Pre-1850 Rushbrooke Hall & Great Barton, 1851 Fever, Frederick & Esther, John & Eliza, Annie Gibb, Tommy (Gallipoli), Harry (Bomber Command DFC), Isabel (Land Girl) & Warren.',
    keywords: ['Denton', 'Dentons', 'Rushbrooke Hall', 'Great Barton', 'Fever of 1851', 'Frederick', 'Esther', 'John Frederick', 'Eliza', 'Annie Gibb', 'Isaac Ephraim', 'Tommy Denton', 'Gallipoli', 'Harry Denton', 'Lancaster', 'DFC', 'Isabel Denton', 'Land Girl', 'Warren Denton', 'Heathfield', 'Greta Valley']
  },
  {
    id: 'ch-gibbs',
    tab: 'gibbs',
    title: 'The Gibb Family — Scottish Lineage & Pioneers',
    category: 'The Gibb Family',
    era: '1850s–1900s',
    summary: 'Scottish lineage, pioneering sheep and pastoral farming across North Canterbury (Motunau, Scargill), and community leadership.',
    keywords: ['Gibb', 'Gibbs', 'Scotland', 'Scottish', 'Motunau', 'Scargill', 'Farming', 'Pastoral', 'Pioneer', 'Canterbury']
  },
  {
    id: 'ch-giles',
    tab: 'giles',
    title: 'The Giles Family — Tradesmen, Builders & Independence',
    category: 'The Giles Family',
    era: '1850s–1900s',
    summary: 'Canterbury artisans, carpenters, bricklayers, township builders, and craftsmanship across Canterbury settlements.',
    keywords: ['Giles', 'Tradesmen', 'Carpenters', 'Builders', 'Craftsmanship', 'Canterbury', 'Artisans']
  },
  {
    id: 'ch-roberts',
    tab: 'roberts',
    title: 'The Roberts Family — Chroniclers & Historians',
    category: 'The Roberts Family',
    era: '1850s–1980s',
    summary: 'Literary memoirs of T.E.L. Roberts and Edward Roberts that documented Canterbury settler heritage and inspired this compilation.',
    keywords: ['Roberts', 'T.E.L. Roberts', 'Edward Roberts', 'Books', 'Historians', 'Memoirs', 'Chroniclers', 'Canterbury']
  },
  {
    id: 'ch-acknowledgement',
    tab: 'acknowledgement',
    title: 'Acknowledgement & Thanks',
    category: 'Acknowledgement',
    era: '2021',
    summary: 'Dedication, acknowledgements to family researchers, photographers, parish registries, and archival contributors.',
    keywords: ['Acknowledgement', 'Thanks', 'Dedication', 'Parish', 'Registries', 'Photographers', 'Contributors', 'Christine Negus']
  }
];

interface SearchResultsProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  members: FamilyMember[];
  stories: Story[];
  archiveItems: ArchiveItem[];
  onSelectMember: (memberId: string) => void;
  onSelectStory: (story: Story) => void;
  onSelectArchiveItem: (item: ArchiveItem) => void;
  setActiveTab: (tab: ArchiveTab) => void;
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  searchQuery,
  setSearchQuery,
  members,
  stories,
  archiveItems,
  onSelectMember,
  onSelectStory,
  onSelectArchiveItem,
  setActiveTab
}) => {
  const [resultCategory, setResultCategory] = useState<'all' | 'chapters' | 'members' | 'stories' | 'archive'>('all');
  const [selectedEraFilter, setSelectedEraFilter] = useState<string>('all');

  // Search logic across Names, Keywords, Dates, Locations, Bios, and Tags
  const searchLower = searchQuery.toLowerCase().trim();

  // 0. Filtered Chapters from Christine Negus compilation
  const matchedChapters = useMemo(() => {
    if (!searchLower) return [];
    return ARCHIVAL_CHAPTERS.filter(ch => {
      const titleMatch = ch.title.toLowerCase().includes(searchLower);
      const categoryMatch = ch.category.toLowerCase().includes(searchLower);
      const summaryMatch = ch.summary.toLowerCase().includes(searchLower);
      const keywordMatch = ch.keywords.some(k => k.toLowerCase().includes(searchLower));
      return titleMatch || categoryMatch || summaryMatch || keywordMatch;
    });
  }, [searchLower]);

  // 1. Filtered Members (Names, Dates, Locations, Bio, Branch)
  const matchedMembers = useMemo(() => {
    if (!searchLower) return [];
    return (members || []).filter(m => {
      if (!m) return false;
      const fullName = `${m.firstName || ''} ${m.lastName || ''} ${m.maidenName || ''}`.toLowerCase();
      const years = `${m.birthYear || ''} ${m.deathYear || ''} ${m.birthDate || ''} ${m.deathDate || ''}`.toLowerCase();
      const place = `${m.birthPlace || ''} ${m.deathPlace || ''}`.toLowerCase();
      const bio = (m.bio || '').toLowerCase();
      const branch = (m.branchTag || '').toLowerCase();
      const occupation = (m.occupation || '').toLowerCase();
      
      const lifeEventsStr = (m.lifeEvents || [])
        .map(e => `${e?.title || ''} ${e?.year || ''} ${e?.location || ''} ${e?.description || ''}`)
        .join(' ')
        .toLowerCase();

      const matchesText = 
        fullName.includes(searchLower) ||
        years.includes(searchLower) ||
        place.includes(searchLower) ||
        bio.includes(searchLower) ||
        branch.includes(searchLower) ||
        occupation.includes(searchLower) ||
        lifeEventsStr.includes(searchLower);

      // Era check
      if (!matchesText) return false;
      if (selectedEraFilter === 'all') return true;
      if (selectedEraFilter === '1800s') return typeof m.birthYear === 'number' && m.birthYear < 1900;
      if (selectedEraFilter === '1900s') return typeof m.birthYear === 'number' && m.birthYear >= 1900 && m.birthYear < 1950;
      if (selectedEraFilter === '1950s') return typeof m.birthYear === 'number' && m.birthYear >= 1950;
      return true;
    });
  }, [members, searchLower, selectedEraFilter]);

  // 2. Filtered Stories (Title, Summary, Content, Year, Decade, Author, Tags)
  const matchedStories = useMemo(() => {
    if (!searchLower) return [];
    return (stories || []).filter(s => {
      if (!s) return false;
      const title = (s.title || '').toLowerCase();
      const summary = (s.summary || '').toLowerCase();
      const content = (s.content || '').toLowerCase();
      const author = (s.authorName || '').toLowerCase();
      const yearStr = `${s.year || ''} ${s.decade || ''}`.toLowerCase();
      const tagsStr = (s.tags || []).join(' ').toLowerCase();

      const matchesText = 
        title.includes(searchLower) ||
        summary.includes(searchLower) ||
        content.includes(searchLower) ||
        author.includes(searchLower) ||
        yearStr.includes(searchLower) ||
        tagsStr.includes(searchLower);

      if (!matchesText) return false;
      if (selectedEraFilter === 'all') return true;
      if (selectedEraFilter === '1800s') return (s.year && s.year < 1900) || s.decade === '1880s' || s.decade === '1890s';
      if (selectedEraFilter === '1900s') return (s.year && s.year >= 1900 && s.year < 1950) || s.decade === '1910s' || s.decade === '1920s' || s.decade === '1930s' || s.decade === '1940s';
      if (selectedEraFilter === '1950s') return (s.year && s.year >= 1950) || s.decade === '1950s' || s.decade === '1960s' || s.decade === '1970s';
      return true;
    });
  }, [stories, searchLower, selectedEraFilter]);

  // 3. Filtered Archive Items (Title, Type, Description, TranscribedText, Year, Decade, Location, Tags)
  const matchedArchiveItems = useMemo(() => {
    if (!searchLower) return [];
    return (archiveItems || []).filter(item => {
      if (!item) return false;
      const title = (item.title || '').toLowerCase();
      const desc = (item.description || '').toLowerCase();
      const text = (item.transcribedText || '').toLowerCase();
      const typeStr = (item.type || '').toLowerCase();
      const location = (item.location || '').toLowerCase();
      const yearStr = `${item.year || ''} ${item.decade || ''}`.toLowerCase();
      const tagsStr = (item.tags || []).join(' ').toLowerCase();

      const matchesText = 
        title.includes(searchLower) ||
        desc.includes(searchLower) ||
        text.includes(searchLower) ||
        typeStr.includes(searchLower) ||
        location.includes(searchLower) ||
        yearStr.includes(searchLower) ||
        tagsStr.includes(searchLower);

      if (!matchesText) return false;
      if (selectedEraFilter === 'all') return true;
      if (selectedEraFilter === '1800s') return (item.year && item.year < 1900) || item.decade === '1880s' || item.decade === '1890s';
      if (selectedEraFilter === '1900s') return (item.year && item.year >= 1900 && item.year < 1950) || item.decade === '1910s' || item.decade === '1920s' || item.decade === '1930s' || item.decade === '1940s';
      if (selectedEraFilter === '1950s') return (item.year && item.year >= 1950) || item.decade === '1950s' || item.decade === '1960s';
      return true;
    });
  }, [archiveItems, searchLower, selectedEraFilter]);

  const totalMatches = matchedChapters.length + matchedMembers.length + matchedStories.length + matchedArchiveItems.length;

  // Helper to highlight search keywords safely in text snippets
  const highlightMatch = (text: string, maxLength: number = 180) => {
    if (!text) return '';
    const truncated = text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
    if (!searchLower) return truncated;

    try {
      const escaped = searchLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const parts = truncated.split(new RegExp(`(${escaped})`, 'gi'));
      return parts.map((part, index) => 
        part.toLowerCase() === searchLower ? (
          <mark key={index} className="bg-amber-200 text-indigo-950 px-1 py-0.5 rounded font-semibold">
            {part}
          </mark>
        ) : (
          part
        )
      );
    } catch {
      return truncated;
    }
  };

  const sampleKeywords = ['Denton', 'Suffolk', 'Gallipoli', 'Annie Gibb', 'Wakefield', '1850', 'Lancaster', 'Gibb', 'Giles', 'Roberts'];

  return (
    <div className="space-y-6">
      
      {/* Search Header Banner (Sleek Indigo & Slate Theme) */}
      <div className="bg-white p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-xs space-y-5 sm:space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold border border-indigo-100 mb-2">
              <Search className="w-3.5 h-3.5 shrink-0" />
              <span>Compilation &amp; Archive Search</span>
            </div>
            <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 tracking-tight break-words">
              {searchQuery ? `Search Results for "${searchQuery}"` : 'Family Record & Profile Search'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              Search chapters, names, dates, historical letters, tags, or birthplaces across all family records.
            </p>
          </div>

          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="min-h-[44px] inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs sm:text-sm transition self-start md:self-auto cursor-pointer"
            >
              <X className="w-4 h-4" />
              <span>Clear Search</span>
            </button>
          )}
        </div>

        {/* Prominent Search Input Box */}
        <div className="relative w-full max-w-3xl">
          <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Type ancestor name, year (e.g. 1850), tag, or keyword..."
            aria-label="Search compilation text, names, and dates"
            className="w-full pl-11 sm:pl-12 pr-20 sm:pr-28 py-3 sm:py-3.5 text-sm sm:text-base md:text-lg bg-slate-50 border-2 border-slate-200 rounded-2xl focus:outline-none focus:border-indigo-600 focus:bg-white text-slate-900 transition shadow-inner min-h-[44px]"
            autoFocus
          />
          {searchQuery ? (
            <button
              onClick={() => setSearchQuery('')}
              className="min-h-[36px] absolute right-2.5 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs font-bold rounded-xl transition cursor-pointer flex items-center justify-center"
            >
              Clear
            </button>
          ) : (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 px-2.5 py-1 bg-indigo-50 text-indigo-700 text-[11px] font-bold rounded-lg border border-indigo-100 hidden sm:block">
              Type to search
            </div>
          )}
        </div>

        {/* Quick Suggestion Chips */}
        <div className="flex flex-wrap items-center gap-2 pt-1 text-xs text-slate-500">
          <span className="font-semibold text-slate-700 mr-1 flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
            Quick Suggestions:
          </span>
          {sampleKeywords.map(kw => (
            <button
              key={kw}
              onClick={() => setSearchQuery(kw)}
              className="min-h-[36px] px-3 py-1.5 rounded-full bg-slate-100 hover:bg-indigo-600 hover:text-white text-slate-700 font-medium transition border border-slate-200 cursor-pointer flex items-center"
            >
              {kw}
            </button>
          ))}
        </div>

        {/* Category Tab Filters */}
        {searchQuery && (
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-between gap-3 border-t border-slate-100 pt-4">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setResultCategory('all')}
                className={`min-h-[44px] px-3.5 sm:px-4 py-2 rounded-xl text-xs md:text-sm font-bold transition flex items-center gap-2 cursor-pointer ${
                  resultCategory === 'all'
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <span>All Results</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${resultCategory === 'all' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-800'}`}>
                  {totalMatches}
                </span>
              </button>

              <button
                onClick={() => setResultCategory('chapters')}
                className={`min-h-[44px] px-3.5 sm:px-4 py-2 rounded-xl text-xs md:text-sm font-bold transition flex items-center gap-2 cursor-pointer ${
                  resultCategory === 'chapters'
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <BookOpen className="w-4 h-4" />
                <span>Chapters ({matchedChapters.length})</span>
              </button>

              <button
                onClick={() => setResultCategory('members')}
                className={`min-h-[44px] px-3.5 sm:px-4 py-2 rounded-xl text-xs md:text-sm font-bold transition flex items-center gap-2 cursor-pointer ${
                  resultCategory === 'members'
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <User className="w-4 h-4" />
                <span>Profiles ({matchedMembers.length})</span>
              </button>

              <button
                onClick={() => setResultCategory('stories')}
                className={`min-h-[44px] px-3.5 sm:px-4 py-2 rounded-xl text-xs md:text-sm font-bold transition flex items-center gap-2 cursor-pointer ${
                  resultCategory === 'stories'
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>Stories ({matchedStories.length})</span>
              </button>

              <button
                onClick={() => setResultCategory('archive')}
                className={`min-h-[44px] px-3.5 sm:px-4 py-2 rounded-xl text-xs md:text-sm font-bold transition flex items-center gap-2 cursor-pointer ${
                  resultCategory === 'archive'
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>Documents &amp; Photos ({matchedArchiveItems.length})</span>
              </button>
            </div>

            {/* Era Filter */}
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-2 rounded-xl border border-slate-200 min-h-[44px]">
              <Filter className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
              <span className="shrink-0">Era:</span>
              <select
                value={selectedEraFilter}
                onChange={e => setSelectedEraFilter(e.target.value)}
                className="bg-transparent font-bold text-indigo-700 cursor-pointer focus:outline-none text-xs sm:text-sm"
              >
                <option value="all">All Eras</option>
                <option value="1800s">1800s Records</option>
                <option value="1900s">1900–1949 Records</option>
                <option value="1950s">1950+ Records</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* SEARCH RESULTS DISPLAY AREA */}
      {!searchQuery ? (
        <div className="bg-white p-12 rounded-3xl border border-slate-200 text-center space-y-4 max-w-2xl mx-auto shadow-xs">
          <div className="w-16 h-16 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto">
            <Search className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-bold font-serif text-slate-900">
            Search Across the Entire Historical Compilation
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            Enter a pioneer name like <strong>Denton</strong> or <strong>Gibb</strong>, a key voyage or year like <strong>1850</strong>, or a topic like <strong>Suffolk</strong> or <strong>Gallipoli</strong> into the search bar above.
          </p>
        </div>
      ) : totalMatches === 0 ? (
        <div className="bg-white p-12 rounded-3xl border border-slate-200 text-center space-y-4 max-w-2xl mx-auto shadow-xs">
          <div className="w-16 h-16 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mx-auto">
            <Search className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-bold font-serif text-slate-900">
            No exact matches found for "{searchQuery}"
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            Try checking spelling, searching for a single surname (e.g. Denton, Gibbs, Giles, Roberts), or a year (e.g. 1850).
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 transition cursor-pointer"
          >
            Reset Search
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          
          {/* SECTION 0: MATCHING ARCHIVAL CHAPTERS */}
          {(resultCategory === 'all' || resultCategory === 'chapters') && matchedChapters.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <h3 className="font-serif text-xl font-bold text-slate-900 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-indigo-600" />
                  <span>Matching Archival Book Chapters ({matchedChapters.length})</span>
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {matchedChapters.map(ch => (
                  <div
                    key={ch.id}
                    onClick={() => {
                      setActiveTab(ch.tab);
                      setSearchQuery('');
                    }}
                    className="bg-[#fbf9f4] p-5 rounded-2xl border-2 border-[#d8ceb9] hover:border-[#7a5b3a] hover:shadow-md transition cursor-pointer flex flex-col justify-between space-y-3 group"
                  >
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-serif font-bold bg-[#e8dec9] text-[#7a5b3a] px-2.5 py-0.5 rounded-full border border-[#d8ceb9]">
                          {ch.category}
                        </span>
                        <span className="text-xs text-slate-500 font-medium font-serif">
                          {ch.era}
                        </span>
                      </div>

                      <h4 className="font-serif font-bold text-lg text-[#2c221e] group-hover:text-[#7a5b3a] transition">
                        {highlightMatch(ch.title)}
                      </h4>

                      <p className="text-xs text-[#4a3525] leading-relaxed">
                        {highlightMatch(ch.summary, 180)}
                      </p>
                    </div>

                    <div className="pt-2 border-t border-[#d8ceb9]/60 flex items-center justify-between text-xs">
                      <span className="text-[#7a5b3a] font-serif italic">
                        Digital Archival Text
                      </span>
                      <span className="font-serif font-bold text-[#7a5b3a] group-hover:translate-x-1 transition flex items-center gap-1">
                        Read Chapter →
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SECTION 1: MATCHING FAMILY MEMBERS */}
          {(resultCategory === 'all' || resultCategory === 'members') && matchedMembers.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <h3 className="font-serif text-xl font-bold text-slate-900 flex items-center gap-2">
                  <User className="w-5 h-5 text-indigo-600" />
                  <span>Matching Family Profiles ({matchedMembers.length})</span>
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {matchedMembers.map(m => {
                  const candidatePhoto = m.image || m.photoUrl;
                  const hasPortrait = isVerifiedHistoricalImage(candidatePhoto);
                  const initials = getPersonInitials(m.firstName, m.lastName);
                  return (
                    <div
                      key={m.id}
                      onClick={() => onSelectMember(m.id)}
                      className="bg-white p-5 rounded-2xl border border-slate-200 hover:border-[#8B263E] hover:shadow-md transition cursor-pointer flex gap-4 group"
                    >
                      {hasPortrait ? (
                        <img
                          src={candidatePhoto}
                          alt={`${m.firstName} ${m.lastName}`}
                          referrerPolicy="no-referrer"
                          className="w-16 h-16 rounded-2xl object-cover border-2 border-[#8B263E]/30 group-hover:scale-105 transition shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#EFE8DD] to-[#DDD2C0] border border-[#8B263E]/40 flex flex-col items-center justify-center shrink-0 p-1 text-center shadow-xs">
                          <span className="font-serif text-base font-bold text-[#8B263E] tracking-wider">
                            {initials}
                          </span>
                          <span className="text-[8px] uppercase tracking-tighter text-[#6E5E55] font-semibold">
                            Archive
                          </span>
                        </div>
                      )}
                      <div className="space-y-1.5 flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-[10px] uppercase tracking-wider font-bold bg-[#8B263E]/10 text-[#8B263E] px-2 py-0.5 rounded-full">
                            {m.branchTag}
                          </span>
                          {m.generation !== undefined && (
                            <span className="text-xs text-slate-500 font-semibold">
                              Gen {m.generation}
                            </span>
                          )}
                        </div>

                        <h4 className="font-serif font-bold text-lg text-slate-900 group-hover:text-[#8B263E] transition truncate">
                          {highlightMatch(`${m.firstName} ${m.lastName}`)}
                          {m.maidenName && <span className="text-slate-500 text-sm font-normal"> ({m.maidenName})</span>}
                        </h4>

                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
                          {m.birthYear && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5 text-[#8B263E]" />
                              {m.birthYear} – {m.deathYear || (m.isLiving ? 'Present' : 'Deceased')}
                            </span>
                          )}
                          {m.birthPlace && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5 text-[#8B263E]" />
                              {highlightMatch(m.birthPlace, 20)}
                            </span>
                          )}
                        </div>

                        {m.bio && (
                          <p className="text-xs text-slate-600 line-clamp-2 pt-1 border-t border-slate-100 mt-2">
                            {highlightMatch(m.bio, 120)}
                          </p>
                        )}

                        <div className="pt-2 flex justify-end">
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-[#8B263E] group-hover:translate-x-1 transition">
                            Open Profile Dossier →
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* SECTION 2: MATCHING STORIES & LETTERS */}
          {(resultCategory === 'all' || resultCategory === 'stories') && matchedStories.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <h3 className="font-serif text-xl font-bold text-slate-900 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-indigo-600" />
                  <span>Matching Stories &amp; Letters ({matchedStories.length})</span>
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {matchedStories.map(story => (
                  <div
                    key={story.id}
                    onClick={() => onSelectStory(story)}
                    className="bg-white p-5 rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition cursor-pointer flex flex-col justify-between space-y-4 group"
                  >
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold bg-amber-50 text-amber-800 px-2.5 py-0.5 rounded-full border border-amber-200 capitalize">
                          {story.category.replace('_', ' ')}
                        </span>
                        <span className="text-xs text-slate-500 font-medium">
                          {story.year ? `Year ${story.year}` : story.decade}
                        </span>
                      </div>

                      <h4 className="font-serif font-bold text-xl text-slate-900 group-hover:text-indigo-600 transition">
                        {highlightMatch(story.title)}
                      </h4>

                      <p className="text-xs text-slate-500 font-medium">
                        Narrated by <strong>{story.authorName}</strong>
                      </p>

                      <p className="text-sm text-slate-700 line-clamp-3">
                        {highlightMatch(story.summary || story.content, 180)}
                      </p>
                    </div>

                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                      <div className="flex flex-wrap gap-1">
                        {story.tags.slice(0, 3).map(t => (
                          <span key={t} className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                            #{t}
                          </span>
                        ))}
                      </div>
                      <span className="font-bold text-indigo-600 group-hover:translate-x-1 transition flex items-center gap-1">
                        Read Story →
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SECTION 3: MATCHING ARCHIVE DOCUMENTS & PHOTOS */}
          {(resultCategory === 'all' || resultCategory === 'archive') && matchedArchiveItems.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <h3 className="font-serif text-xl font-bold text-slate-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-600" />
                  <span>Matching Archive Vault Items ({matchedArchiveItems.length})</span>
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {matchedArchiveItems.map(item => (
                  <div
                    key={item.id}
                    onClick={() => onSelectArchiveItem(item)}
                    className="bg-white p-4 rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition cursor-pointer flex flex-col justify-between space-y-3 group"
                  >
                    <div className="space-y-2">
                      <div className="relative h-44 rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
                        <img
                          src={item.fileUrl}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                        />
                        <span className="absolute top-2 left-2 px-2.5 py-1 rounded-lg bg-slate-900/80 text-white text-[11px] font-bold backdrop-blur-xs capitalize">
                          {item.type}
                        </span>
                        {item.decade && (
                          <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-white/90 text-slate-900 text-[10px] font-extrabold shadow-xs">
                            {item.decade}
                          </span>
                        )}
                      </div>

                      <h4 className="font-serif font-bold text-base text-slate-900 group-hover:text-indigo-600 transition line-clamp-1">
                        {highlightMatch(item.title)}
                      </h4>

                      <p className="text-xs text-slate-600 line-clamp-2">
                        {highlightMatch(item.description, 100)}
                      </p>

                      {item.transcribedText && (
                        <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200 text-[11px] text-slate-600 italic">
                          "{highlightMatch(item.transcribedText, 80)}"
                        </div>
                      )}
                    </div>

                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                      <span className="text-slate-500 font-medium">
                        {item.location || 'Archived record'}
                      </span>
                      <span className="font-bold text-indigo-600 group-hover:translate-x-1 transition">
                        View Document →
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}

    </div>
  );
};
